/** The agent reads and calls, as TanStack Query. Local process, so no retry and no stale window. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { MachineRecord } from "@nebulize/sdk";

import { api, isSignedOut } from "@/lib/api";
import { spaces } from "@/lib/spaces";

import { agentApi, type PairingScreen, type StatusResponse } from "./client";

export const STATUS_KEY = ["agent", "status"] as const;
export const PAIRING_KEY = ["agent", "pairing"] as const;

/** How often `GET /status` is re-read while a screen is open. */
export const STATUS_POLL_MS = 3_000;

export function useAgentStatus() {
  return useQuery<StatusResponse>({
    queryKey: STATUS_KEY,
    queryFn: agentApi.status,
    refetchInterval: STATUS_POLL_MS,
    staleTime: 0,
    retry: false,
  });
}

export function useAgentPairing() {
  return useQuery<PairingScreen>({
    queryKey: PAIRING_KEY,
    queryFn: agentApi.pairing,
    staleTime: 0,
    retry: false,
  });
}

/** Every pairing call answers with the whole screen, so the reply seeds the cache. */
export function usePairingCall(call: () => Promise<PairingScreen>) {
  const queryClient = useQueryClient();
  return useMutation<PairingScreen>({
    mutationFn: call,
    onSuccess: (screen) => queryClient.setQueryData(PAIRING_KEY, screen),
  });
}

/**
 * The machines the signed-in account holds, read so a screen can tell whether THIS one is among
 * them.
 *
 * The agent screens sit between two answers that have never been compared: the machine in front of
 * you says which account approved it, and the browser says who you are signed in as. Nothing put
 * those side by side, so somebody signed in as one account, looking at a machine paired to
 * another, saw a page that looked entirely healthy and a machine that would never answer for them.
 */
export function useAccountMachines() {
  return useQuery<MachineRecord[]>({
    queryKey: ["account", "machines"],
    queryFn: () => api.get<MachineRecord[]>("/v1/accounts/me/machines"),
    retry: false,
  });
}

/** Whether this machine belongs to whoever is signed in. Unknown while either answer is missing. */
export type MachineOwnership =
  "unknown" | "signed_out" | "mine" | "somebody_else";

export function useMachineOwnership(
  machineId: string | null | undefined,
): MachineOwnership {
  const machines = useAccountMachines();
  if (isSignedOut(machines.error)) return "signed_out";
  if (!machineId || machines.data === undefined) return "unknown";
  return machines.data.some((one) => one.machine_id === machineId)
    ? "mine"
    : "somebody_else";
}

/**
 * The name of the space this machine is serving, read from the account rather than the machine.
 *
 * The agent holds an id because that is all a machine needs. A person reading a screen needs the
 * name, and the browser is the half of this pair that has a session to ask with.
 */
export function useServedSpaceName(
  spaceId: string | null | undefined,
): string | null {
  const space = useQuery({
    queryKey: ["agent", "served-space", spaceId],
    queryFn: () => spaces.read(spaceId as string),
    enabled: Boolean(spaceId),
    retry: false,
  });
  return space.data?.name ?? null;
}
